/**
 * Calendar views, their date ranges and locale formatting (dates and numbers only through the i18n Intl helpers,
 * DESIGN.md §17).
 */
import { useI18n } from '@platform/i18n';
import { addDays, calendarWeek, monthMatrix, sameDay, startOfDay, startOfWeek, weekRulesFor } from '@platform/ui';
import { useContext, useMemo } from 'react';
import { CalendarContext } from './calendar-context';

export const CALENDAR_VIEWS = ['day', 'threeDay', 'workWeek', 'week', 'custom', 'month', 'agenda'] as const;
export type CalendarView = (typeof CALENDAR_VIEWS)[number];

/** Days of the agenda list. */
export const AGENDA_DAYS = 14;

/** The same regional week convention used by shared date pickers. Explicit calendar settings can override the layout. */
export function weekStartFor(locale: string): number {
  return weekRulesFor(locale, { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, regionLocale: navigator.language }).weekStartsOn;
}

export interface ViewRange {
  start: Date;
  /** Exclusive. */
  end: Date;
  /** Columns of the time grid (day/week views). */
  days: Date[];
}

/** `customDays`: length of the "N days" view (setting). */
export function viewRange(view: CalendarView, date: Date, weekStart: number, customDays = 4): ViewRange {
  const day = startOfDay(date);
  const span = (start: Date, count: number): ViewRange => {
    const days = Array.from({ length: count }, (_, i) => addDays(start, i));
    return { start, end: addDays(start, count), days };
  };
  switch (view) {
    case 'day':
      return span(day, 1);
    case 'threeDay':
      return span(day, 3);
    case 'custom':
      return span(day, customDays);
    case 'workWeek':
      return span(startOfWeek(day, 1), 5);
    case 'week':
      return span(startOfWeek(day, weekStart), 7);
    case 'agenda':
      return span(day, AGENDA_DAYS);
    case 'month': {
      const weeks = monthMatrix(day, weekStart);
      const first = weeks[0]?.[0] ?? day;
      return { start: first, end: addDays(first, 42), days: weeks.flat() };
    }
  }
}

/** Moves the shown date one view step (Outlook: Alt+←/→). */
export function stepDate(view: CalendarView, date: Date, direction: 1 | -1, customDays = 4): Date {
  if (view === 'month') return new Date(date.getFullYear(), date.getMonth() + direction, 1);
  const days = { day: 1, threeDay: 3, custom: customDays, workWeek: 7, week: 7, agenda: AGENDA_DAYS }[view];
  return addDays(date, days * direction);
}

/** Length of a range, e.g. "2 h 30 min", "2 days 8 h"; all-day ranges in whole days ("3 days"). */
export function durationParts(start: Date, end: Date, allDay: boolean): { days: number; hours: number; minutes: number } {
  if (allDay) return { days: Math.max(1, Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / 86_400_000)), hours: 0, minutes: 0 };
  const total = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60_000));
  return { days: Math.floor(total / 1440), hours: Math.floor((total % 1440) / 60), minutes: total % 60 };
}

export function useCalendarFormat() {
  const { formatDate, formatNumber, locale, t } = useI18n();
  // Outside the calendar (mail meeting card) there are no calendar settings: language defaults.
  const settings = useContext(CalendarContext)?.settings;
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const weekTimeZone = settings?.displayTimeZone ?? browserZone;
  const regionLocale = navigator.language;
  const weekRules = useMemo(() => weekRulesFor(locale, { timeZone: weekTimeZone, regionLocale, numbering: settings?.weekNumbering ?? 'regional' }), [locale, weekTimeZone, regionLocale, settings?.weekNumbering]);
  const weekStart = settings?.weekStart ?? weekRules.weekStartsOn;
  const hourCycle = settings?.timeFormat === 'h12' ? 'h12' : settings?.timeFormat === 'h24' ? 'h23' : undefined;
  return useMemo(() => {
    const cycle: Intl.DateTimeFormatOptions = hourCycle ? { hourCycle } : {};
    const between = (start: string, end: string) => t('webmail.calendar.range', { start, end });
    const time = (d: Date) => formatDate(d, { hour: 'numeric', minute: '2-digit', ...cycle });
    const dayLabel = (d: Date) => formatDate(d, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const shortDay = (d: Date) => formatDate(d, { weekday: 'short', day: 'numeric', month: 'short' });
    const duration = (start: Date, end: Date, allDay: boolean) => {
      const { days, hours, minutes } = durationParts(start, end, allDay);
      const parts = [
        days > 0 && t('webmail.calendarUx.duration.days', { n: days }),
        hours > 0 && t('webmail.calendarUx.duration.hours', { n: hours }),
        (minutes > 0 || (days === 0 && hours === 0)) && t('webmail.calendarUx.duration.minutes', { n: minutes }),
      ].filter((part): part is string => typeof part === 'string');
      return new Intl.ListFormat(locale, { type: 'unit', style: 'narrow' }).format(parts);
    };
    const range = (start: Date, end: Date, allDay: boolean) => {
      if (allDay) {
        const last = addDays(end, -1);
        return sameDay(start, last) || last < start ? shortDay(start) : between(shortDay(start), shortDay(last));
      }
      const long = (d: Date) => formatDate(d, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', ...cycle });
      return sameDay(start, end) || (end.getTime() === startOfDay(end).getTime() && sameDay(start, addDays(end, -1))) ? between(time(start), time(end)) : between(long(start), long(end));
    };
    return {
      locale,
      weekStart,
      weekTimeZone,
      weekNumber: (day: Date) => t('webmail.calendarUx.weekNumber', { n: formatNumber(calendarWeek(day, weekRules).week) }),
      rowWeekNumber: (rowStart: Date) => t('webmail.calendarUx.weekNumber', { n: formatNumber(calendarWeek(addDays(rowStart, 3), weekRules).week) }),
      weekInfo: (day: Date) => calendarWeek(day, weekRules),
      duration,
      /** "09:00 – 11:30 · 2 h 30 min" (selection label, quick create). */
      selection: (start: Date, end: Date, allDay: boolean) => t('webmail.calendarUx.selection', { range: range(start, end, allDay), duration: duration(start, end, allDay) }),
      list: (items: readonly string[]) => new Intl.ListFormat(locale, { type: 'conjunction' }).format(items),
      time,
      dayLabel,
      shortDay,
      weekday: (d: Date) => formatDate(d, { weekday: 'short' }),
      weekdayNarrow: (d: Date) => formatDate(d, { weekday: 'narrow' }),
      dayNumber: (d: Date) => formatNumber(d.getDate()),
      month: (d: Date) => formatDate(d, { month: 'long', year: 'numeric' }),
      /** "13 October". */
      dayMonth: (d: Date) => formatDate(d, { day: 'numeric', month: 'long' }),
      hour: (h: number) => formatDate(new Date(2000, 0, 1, h), { hour: 'numeric', ...cycle }),
      /** "09:00 – 10:30", with dates when the event spans days; all-day: the date(s). */
      range,
      /** Command bar label of the shown range (`compact`: phones). */
      rangeLabel: (view: CalendarView, range: ViewRange, date: Date, compact = false) => {
        if (view === 'day') return compact ? shortDay(date) : dayLabel(date);
        if (view === 'month') return formatDate(date, { month: 'long', year: 'numeric' });
        const last = addDays(range.end, -1);
        return between(formatDate(range.start, { day: 'numeric', month: 'short' }), formatDate(last, { day: 'numeric', month: 'short', year: 'numeric' }));
      },
    };
  }, [formatDate, formatNumber, locale, t, weekStart, hourCycle, weekRules, weekTimeZone]);
}

export type CalendarFormat = ReturnType<typeof useCalendarFormat>;
